import React, { useState } from 'react';
import {
  Stack,
  Text,
  Spinner,
  MessageBar,
  MessageBarType,
  TooltipHost,
  ITooltipHostStyles,
  DefaultButton,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { useSubject } from '../Contexts/SubjectContext';
import { useEmployee } from '../Contexts/EmployeeContext';
import { uploadProgressFile } from 'src/endpoints/postFile';
import { Button } from '@fluentui/react-components';

const stackTokens = { childrenGap: 20, padding: 20 };
const columnTokens = { childrenGap: 10 };

const columnStyle = {
  root: {
    border: '1px solid #ccc',
    padding: 10,
    borderRadius: 5,
    width: '100%',
    boxSizing: 'border-box' as const,
  },
};

const ProgressUpdates: React.FC = () => {
  const { subject } = useSubject();
  const { employee } = useEmployee();
  const navigate = useNavigate();

  // Semester start date
  const semesterStartDate = new Date('2025-01-20');

  // Function to calculate the current week of the semester
  const getCurrentWeek = (): number => {
    const today = new Date();
    const diffInMilliseconds = today.getTime() - semesterStartDate.getTime();
    const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    return Math.ceil((diffInDays + 1) / 7); // Add 1 to include the start day
  };

  const currentWeek = getCurrentWeek();

  // States for each progress file
  const [progress1, setProgress1] = useState<File | null>(null);
  const [progress2, setProgress2] = useState<File | null>(null);
  const [progress3, setProgress3] = useState<File | null>(null);

  // Upload state and feedback message
  const [uploading, setUploading] = useState<{ [key: number]: boolean }>({
    1: false,
    2: false,
    3: false,
  });
  const [uploadMessage, setUploadMessage] = useState<string>('');

  // Availability dates for each progress update
  const progressAvailability: { [key: number]: Date } = {
    1: new Date('2025-03-01'),
    2: new Date('2025-04-10'),
    3: new Date('2025-05-15'),
  };

  // Allowed file extensions
  const allowedExtensions = ['pdf', 'xlsx', 'docx'];

  if (!subject.SubjectName || !subject.Group || !subject.SubjectCode) {
    navigate(-1);
  }

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    progressNumber: number
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        alert(
          `Formato inválido. Solo se permiten archivos: ${allowedExtensions
            .map((ext) => '.' + ext)
            .join(', ')}`
        );
        return;
      }
      if (progressNumber === 1) setProgress1(file);
      else if (progressNumber === 2) setProgress2(file);
      else if (progressNumber === 3) setProgress3(file);
    }
  };

  const isProgressAvailable = (progressNumber: number): boolean => {
    const today = new Date();
    return today >= progressAvailability[progressNumber];
  };

  const handleUpload = async (progressNumber: number) => {
    let fileToUpload: File | null = null;
    if (progressNumber === 1) fileToUpload = progress1;
    else if (progressNumber === 2) fileToUpload = progress2;
    else if (progressNumber === 3) fileToUpload = progress3;

    if (!isProgressAvailable(progressNumber)) {
      alert(
        `El avance ${progressNumber} aún no está disponible. Disponible a partir de ${progressAvailability[progressNumber].toLocaleDateString()}`
      );
      return;
    }

    if (fileToUpload) {
      setUploading((prev) => ({ ...prev, [progressNumber]: true }));
      setUploadMessage('');
      try {
        console.log(`Uploading Avance ${progressNumber}:`, fileToUpload);
        await uploadProgressFile(
          fileToUpload,
          employee.EmployeeId,
          progressNumber.toString(),
          subject
        );
        setUploadMessage(`Avance ${progressNumber} subido exitosamente.`);
      } catch (error) {
        console.error(`Error uploading avance ${progressNumber}:`, error);
        setUploadMessage(`Error al subir Avance ${progressNumber}.`);
      } finally {
        setUploading((prev) => ({ ...prev, [progressNumber]: false }));
      }
    } else {
      alert(`Seleccione un archivo para el Avance ${progressNumber}`);
    }
  };

  const tooltipStyles: Partial<ITooltipHostStyles> = {
    root: { display: 'inline-block' },
  };

  return (
    <Stack tokens={stackTokens}>
      <Stack horizontal horizontalAlign="space-between">
        <Button
          appearance="secondary"
          size="large"
          style={{
            backgroundColor: '#f5f5f5',
            border: '1px solid #ccc',
          }}
          onClick={() => navigate(-1)}
        >
          Volver
        </Button>
        <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
          Semana {currentWeek}
        </Text>
      </Stack>

      <Text variant="xLarge">Subir Avances</Text>

      {subject && (
        <Stack horizontal tokens={{ childrenGap: 20 }}>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Materia: {subject.SubjectName}
          </Text>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Grupo: {subject.Group}
          </Text>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Clave Materia: {subject.SubjectCode}
          </Text>
          <Text variant="xLarge" styles={{ root: { fontWeight: 'bold' } }}>
            Plan: {subject.Plan}
          </Text>
        </Stack>
      )}

      {/* Sección de Avances */}
      <Stack horizontal tokens={{ childrenGap: 20 }}>
        {[1, 2, 3].map((progressNumber) => (
          <Stack
            key={progressNumber}
            tokens={columnTokens}
            styles={columnStyle}
          >
            <Text variant="large">{`Avance ${progressNumber}`}</Text>
            <TooltipHost
              content={`Formato permitido: ${allowedExtensions
                .map((ext) => '.' + ext)
                .join(', ')}`}
              styles={tooltipStyles}
            >
              <input
                type="file"
                accept={allowedExtensions.map((ext) => '.' + ext).join(',')}
                onChange={(e) => handleFileChange(e, progressNumber)}
                disabled={!isProgressAvailable(progressNumber)}
              />
            </TooltipHost>

            {!isProgressAvailable(progressNumber) && (
              <Text variant="small" styles={{ root: { color: 'red' } }}>
                Disponible a partir de{' '}
                {progressAvailability[progressNumber].toLocaleDateString(
                  'es-MX'
                )}
              </Text>
            )}

            <Button
              appearance="primary"
              onClick={() => handleUpload(progressNumber)}
              disabled={
                !isProgressAvailable(progressNumber) ||
                uploading[progressNumber]
              }
            >
              {`Subir Avance ${progressNumber}`}
            </Button>

            {uploading[progressNumber] && <Spinner label="Subiendo..." />}
          </Stack>
        ))}
      </Stack>

      <Stack
        tokens={{ childrenGap: 10 }}
        horizontalAlign="center"
        styles={{ root: { marginTop: 20 } }}
      >
        <Text variant="xLarge">
          Puedes descargar un formato de avance ejemplo
        </Text>
        <DefaultButton
          text="Descargar Ejemplo"
          onClick={() => {
            const link = document.createElement('a');
            link.href = '/EjemploReporteAvance.xlsx';
            link.download = 'EjemploReporteAvance.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
        />
      </Stack>

      {uploadMessage && (
        <MessageBar
          messageBarType={
            uploadMessage.includes('Error')
              ? MessageBarType.error
              : MessageBarType.success
          }
        >
          {uploadMessage}
        </MessageBar>
      )}
    </Stack>
  );
};

export default ProgressUpdates;
